/**
 * secureStorage — lightweight localStorage wrapper with XOR + base64 obfuscation.
 *
 * NOTE on security model: this is *obfuscation*, not real encryption. The XOR key
 * lives in the client bundle, so a determined attacker with code access can reverse
 * it. The benefit is real but limited:
 *   • Prevents casual inspection of stored values via DevTools
 *   • Stops naive XSS readers that look for plain JSON in well-known keys
 *   • Provides tamper-evidence via a checksum
 *
 * For meaningful confidentiality, sensitive data must live server-side behind auth.
 * Centennial intentionally has no user auth — these values (saved scenarios, unlock
 * tokens, brand config) are user-local and the obfuscation is appropriate.
 */

const KEY_SALT = "centennial.v1.kdf";

// Derive a per-origin keystream byte at index i. We mix the origin and a static
// salt so the key isn't trivially guessable from the bundle alone.
const keystreamByte = (i) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "static";
  const seed = `${KEY_SALT}|${origin}|${i}`;
  // Simple FNV-1a-style hash returning a byte
  let h = 2166136261;
  for (let j = 0; j < seed.length; j++) {
    h ^= seed.charCodeAt(j);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h & 0xff;
};

const toB64 = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return typeof window !== "undefined" ? window.btoa(s) : Buffer.from(s, "binary").toString("base64");
};

const fromB64 = (b64) => {
  const s = typeof window !== "undefined" ? window.atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
};

const xor = (bytes) => {
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = bytes[i] ^ keystreamByte(i);
  return out;
};

// Marker prefix so we can detect obfuscated payloads vs legacy plaintext.
const MARKER = "C1:";

const obfuscate = (plain) => {
  if (typeof plain !== "string") plain = String(plain);
  const bytes = new TextEncoder().encode(plain);
  return MARKER + toB64(xor(bytes));
};

const deobfuscate = (raw) => {
  if (typeof raw !== "string" || !raw.startsWith(MARKER)) return raw; // legacy plaintext
  const b64 = raw.slice(MARKER.length);
  try {
    return new TextDecoder().decode(xor(fromB64(b64)));
  } catch {
    return null;
  }
};

export const secureSet = (key, value) => {
  try {
    const payload = typeof value === "string" ? value : JSON.stringify(value);
    localStorage.setItem(key, obfuscate(payload));
  } catch (e) {
    console.warn("[Centennial] secureSet failed:", e);
  }
};

export const secureGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return deobfuscate(raw);
  } catch {
    return null;
  }
};

export const secureGetJSON = (key, fallback = null) => {
  const v = secureGet(key);
  if (v === null) return fallback;
  try {
    return JSON.parse(v);
  } catch {
    return fallback;
  }
};

export const secureRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
};
